// MST
import { types, flow, detach, cast } from 'mobx-state-tree';

// Interfaces
import {
  IAssessmentCancelAttrs,
  IAssessmentDecisionFormListAttrs,
  IAssessmentDecisionSubmitAttrs,
  IAssessmentFilterPhaseListAttrs,
  IAssessmentFilterStatusListAttrs,
  IAssessmentGetAssessmentPhaseListAttrs,
  IAssessmentGetAssessmentStartDetailAttrs,
  IAssessmentGetDetailAttrs,
  IAssessmentGetListAttrs,
  IAssessmentGetProgressDetailAttrs,
  IAssessmentSubmitAssessmentStartDetailAttrs,
  IAssessmentUpdateAssessmentStartDetailAttrs,
} from 'interfaces/back-office/assessment/assessment-view.interface';
import {
  IAssessmentApiDetailResponse,
  IAssessmentApiListResponse,
  IAssessmentApiProgressDetailResponse,
  IAssessmentApiAssessmentPhaseListResponse,
  IAssessmentApiStartAssessmentDetailResponse,
  IAssessmentApiUpdateStartAssessmentDetailResponse,
  IAssessmentApiSubmitStartAssessmentDetailResponse,
  IAssessmentApiDecisionPhaseListResponse,
  IAssessmentApiFilterPhaseListResponse,
} from 'interfaces/back-office/assessment/assessment-api.interface';
import { ISharedCommonLoadingAttrs } from 'interfaces/shared/shared-common.interface';
import { IAssessment } from 'interfaces/back-office/assessment';

// Extensions
import { withEnvironment } from 'models/extensions/with-environment';
import {
  withHelper_assessmentScheduleModel,
  withHelper_commonLoadingModel,
  withHelper_commonMapLoading,
  withHelper_competencyUnitProps,
} from 'models/extensions/helper';
import {
  withHelper_companyDataProps,
  withHelper_paginationProps,
  withHelper_profileDataProps,
} from 'models/extensions/with-helper';

// Lodash
import cloneDeep from 'lodash/cloneDeep';
import orderBy from 'lodash/orderBy';

// Constant
import { ASSESSMENT_STATUS, ASSESSMENT_PHASES, LOADING } from 'constant/shared.constant';

// Utils
import { commonUtils_preventDuplicate } from 'utils/common/common.utils';

// Loading
const { BASE, DETAIL, ACTION } = LOADING;

// Types
const { optional, model, maybeNull, string, number, array, enumeration, boolean } = types;

const ProcessList = optional(
  array(
    model('ProcessList').props({
      id: maybeNull(number),
      name: maybeNull(string),
      date: maybeNull(string),
    }),
  ),
  [],
);

const PhaseList = optional(
  array(
    model('PhaseList').props({
      id: maybeNull(number),
      name: maybeNull(enumeration('Phase', Object.values(ASSESSMENT_PHASES))),
      date: maybeNull(string),
    }),
  ),
  [],
);

const AssessmentList = optional(
  array(
    model('AssessmentList').props({
      id: maybeNull(number),
      fieldName: maybeNull(string),
      certificationId: maybeNull(number),
      certificationName: maybeNull(string),
      phase: maybeNull(enumeration('Phase', Object.values(ASSESSMENT_PHASES))),
      status: maybeNull(enumeration('Status', Object.values(ASSESSMENT_STATUS))),
      createdAt: maybeNull(string),
      schedule: maybeNull(withHelper_assessmentScheduleModel),
      phases: PhaseList,
      processes: ProcessList,

      // ! For Front-End Only
      loading: withHelper_commonLoadingModel,
    }),
  ),
  [],
);

const AssessmentPaginated = optional(
  model('AssessmentPaginated').props({ ...withHelper_paginationProps, list: AssessmentList }),
  {},
);

const AssessmentUserDetail = optional(
  model('AssessmentUserDetail').props({
    profileData: optional(model('ProfileData').props(withHelper_profileDataProps), {}),
    companyData: optional(model('CompanyData').props(withHelper_companyDataProps), {}),
  }),
  {},
);

const FormAplTwo = optional(
  model('FormAplTwo').props({
    competentSummary: maybeNull(number),
    incompetentSummary: maybeNull(number),
  }),
  {},
);

const AssessmentDetail = optional(
  model('AssessmentDetail').props({
    assesseeSubmitFormAss: maybeNull(string),
    assesseeSubmitFormDec: maybeNull(string),
    assesseeSubmitFormPrep: maybeNull(string),
    assessorSubmitFormAss: maybeNull(string),
    assessorSubmitFormPrep: maybeNull(string),
    id: maybeNull(number),
    certificationId: maybeNull(number),
    certificationName: maybeNull(string),
    certificationCode: maybeNull(string),
    purposeOfAssessment: maybeNull(string),
    isAssesseeStartAss: maybeNull(boolean),
    isRelevantProof: maybeNull(boolean),
    isAssesseeSubmitAssessment: maybeNull(boolean),
    phase: maybeNull(enumeration('Phase', Object.values(ASSESSMENT_PHASES))),
    status: maybeNull(enumeration('Status', Object.values(ASSESSMENT_STATUS))),
    assessorName: maybeNull(string),
    user: AssessmentUserDetail,
    competencyUnits: optional(
      array(model('CompetencyUnit').props({ ...withHelper_competencyUnitProps, standardization: maybeNull(string) })),
      [],
    ),
    feedbackNotes: maybeNull(string),
    formAplTwo: FormAplTwo,
    phases: PhaseList,
    isCompetent: maybeNull(boolean),
    rejectReason: maybeNull(string),
    tuk: maybeNull(string),
    noReg: maybeNull(string)
  }),
  {},
);

const loadingAssessmentPhaseList = optional(
  model('Loading').props({
    isLoading: optional(boolean, false),
    isDetailLoading: optional(boolean, false),
    isActionLoading: optional(boolean, false),
    isDownloadLoading: optional(boolean, false),
    isDropdownLoading: optional(boolean, false),
  }),
  {},
);

const assessmentUploadFile = optional(
  model('assessmentUploadFile').props({
    blobId: maybeNull(string),
    originalFileName: maybeNull(string),
    status: maybeNull(string),
    uploadDate: maybeNull(string),
    userFileBlobId: maybeNull(string),
    userOriginalFileName: maybeNull(string),
  }),
  {},
);

const assessmentPhaseList = optional(
  model('AssessmentPhaseList').props({
    id: maybeNull(number),
    isDownloadFromPortal: maybeNull(boolean),
    isFilledbyAssessee: maybeNull(boolean),
    isGeneratefromCms: maybeNull(boolean),
    isGeneratefromPortal: maybeNull(boolean),
    name: maybeNull(string),
    description: maybeNull(string),
    uploadFiles: array(assessmentUploadFile),
    TTDAssesse: maybeNull(assessmentUploadFile),
    loading: loadingAssessmentPhaseList,
  }),
  {},
);

const answerOptionCompUnit = optional(
  model('answerOptionCompUnit').props({
    id: maybeNull(string),
    answerOptionType: maybeNull(string),
    label: maybeNull(string),
    value: maybeNull(string),
    isSelected: maybeNull(boolean),
  }),
  {},
);

const answerCompUnit = optional(
  model('answerCompUnit').props({
    answerCode: maybeNull(string),
    answerName: maybeNull(string),
    answerLayout: maybeNull(string),
    answerType: maybeNull(string),
    answerValue: maybeNull(string),
    answerFiles: maybeNull(array(string)),
    answerOption: array(answerOptionCompUnit),
  }),
  {},
);

const questionCompUnit = optional(
  model('questionCompUnit').props({
    questionId: maybeNull(number),
    isQuestionTableBordered: maybeNull(boolean),
    isQuestionBordered: maybeNull(boolean),
    questionType: maybeNull(string),
    question: maybeNull(string),
    questionFileUrl: maybeNull(string),
  }),
  {},
);

const resultDetailCompUnit = optional(
  model('resultDetailCompUnit').props({
    question: maybeNull(questionCompUnit),
    answer: maybeNull(answerCompUnit),
    headerId: maybeNull(number),
    id: maybeNull(number),
    childrenHeaderId: maybeNull(number),
    type: maybeNull(string),
  }),
  {},
);

const resultCompUnit = optional(
  model('resultCompUnit').props({
    kukNumber: maybeNull(string),
    resultDetail: array(resultDetailCompUnit),
  }),
  {},
);

const headerCompUnit = optional(
  model('headerCompUnit').props({
    id: maybeNull(number),
    title: maybeNull(string),
    type: maybeNull(string),
  }),
  {},
);

const dataCompUnit = optional(
  model('dataCompUnit').props({
    questionSetId: maybeNull(number),
    questionSetLayout: maybeNull(string),
    instruction: maybeNull(string),
    header: array(headerCompUnit),
    result: array(resultCompUnit),
  }),
  {},
);

const formQuestionSet = optional(
  model('formQuestionSet').props({
    data: array(dataCompUnit),
  }),
  {},
);

const competencyUnit = optional(
  model('compUnit').props({
    competencyUnitId: maybeNull(number),
    competencyUnitCode: maybeNull(string),
    competencyUnitTitle: maybeNull(string),
    competencyUnitName: maybeNull(string),
    isFinished: maybeNull(boolean),
    formQuestionSet: array(formQuestionSet),
    feedbackNotes: maybeNull(string)
  }),
  {},
);

const startAssessmentDetail = optional(array(competencyUnit), []);

const decisionFormList = optional(
  model('decisionFormList').props({
    id: maybeNull(number),
    isDownloadFromPortal: maybeNull(boolean),
    isFilledbyAssessee: maybeNull(boolean),
    isGeneratefromCms: maybeNull(boolean),
    isGeneratefromPortal: maybeNull(boolean),
    name: maybeNull(string),
    description: maybeNull(string),
    uploadFiles: array(assessmentUploadFile),
    TTDAssesse: maybeNull(assessmentUploadFile),
    loading: loadingAssessmentPhaseList,
  }),
  {},
);

const phaseList = model('phaseList').props({
  value: maybeNull(string),
  label: string,
});

// Base Model
const AssessmentStoreModel = model('Assessment')
  .props({
    assessment_loading: withHelper_commonLoadingModel,
    assessment_list: AssessmentPaginated,
    assessment_detail: AssessmentDetail,
    assessment_phase_list: optional(array(assessmentPhaseList), []),
    start_Assessment_Detail: startAssessmentDetail,
    unit_competency_detail: competencyUnit,
    decision_form_list: optional(array(decisionFormList), []),
    filter_phase_list: optional(array(phaseList), []),
    filter_status_list: optional(array(phaseList), []),
  })
  .actions(self => ({
    assessment_SET_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = withHelper_commonMapLoading(self.assessment_loading, payload);

      detach(self.assessment_loading);
      self.assessment_loading = cast(map);
    },
    assessment_SET_FILTER_PHASE_LIST: (payload: IAssessmentApiFilterPhaseListResponse['res']['data']): void => {
      const map = payload.map(el => ({
        value: el.value,
        label: el.label || '',
      }));
      self.filter_phase_list = cast(map);
    },
    assessment_SET_FILTER_STATUS_LIST: (payload: IAssessmentApiFilterPhaseListResponse['res']['data']): void => {
      const map = payload.map(el => ({
        value: el.value,
        label: el.label || '',
      }));
      self.filter_status_list = cast(map);
    },
    assessment_SET_LIST: (
      payload: IAssessmentApiListResponse['res']['data'],
      filter: { phase?: ASSESSMENT_PHASES; status?: ASSESSMENT_STATUS },
    ): void => {
      let list: IAssessment[] = commonUtils_preventDuplicate([...self.assessment_list.list, ...payload.list], 'id');

      // Check if there any phase filter
      if (filter.phase) {
        list = list.filter(list => list.phase === filter.phase);
      }

      // Check if there any status filter
      if (filter.status) {
        list = list.filter(list => list.status === filter.status);
      }

      const map = {
        ...payload,
        list: orderBy(list, ['createdAt'], ['desc']),
      };

      detach(self.assessment_list);
      self.assessment_list = cast(cloneDeep(map));
    },
    assessment_SET_ASSESSMENT_PHASE_LIST_LOADING: (value: boolean, preparationId: number): void => {
      const map = cloneDeep(
        self.assessment_phase_list.map(el => {
          if (el.id === preparationId) {
            return {
              ...el,
              loading: {
                isActionLoading: value,
                isDetailLoading: value,
                isDownloadLoading: value,
                isDropdownLoading: value,
                isLoading: value,
              },
            };
          } else {
            return {
              ...el,
              loading: {
                isActionLoading: false,
                isDetailLoading: false,
                isDownloadLoading: false,
                isDropdownLoading: false,
                isLoading: false,
              },
            };
          }
        }),
      );
      detach(self.assessment_phase_list);
      self.assessment_phase_list = cast(map);
    },
    assessment_SET_DECISION_FORM_LIST_LOADING: (value: boolean, preparationId: number): void => {
      const map = cloneDeep(
        self.decision_form_list.map(el => {
          if (el.id === preparationId) {
            return {
              ...el,
              loading: {
                isActionLoading: value,
                isDetailLoading: value,
                isDownloadLoading: value,
                isDropdownLoading: value,
                isLoading: value,
              },
            };
          } else {
            return {
              ...el,
              loading: {
                isActionLoading: false,
                isDetailLoading: false,
                isDownloadLoading: false,
                isDropdownLoading: false,
                isLoading: false,
              },
            };
          }
        }),
      );
      detach(self.decision_form_list);
      self.decision_form_list = cast(map);
    },
    assessment_SET_ASSESSMENT_PHASE_LIST: (payload: IAssessmentApiAssessmentPhaseListResponse['res']['data']): void => {
      self.assessment_phase_list = cast(payload);
      const map = cloneDeep(
        self.assessment_phase_list.map(el => {
          return { ...el, TTDAssesse: el.uploadFiles.find(el => el.status === 'TTD Assessee') };
        }),
      );
      self.assessment_phase_list = cast(map);
    },
    assessment_SET_START_ASSESSMENT_DETAIL: (
      payload: IAssessmentApiStartAssessmentDetailResponse['res']['data'],
      compId: number,
    ): void => {
      const unitComp = payload[0].unitCompetencys[compId - 1];
      self.start_Assessment_Detail = cast(payload[0].unitCompetencys);
      self.unit_competency_detail = cast(unitComp);
    },
    assessment_SET_UNIT_COMPETENCY_DETAIL: (payload: { ucId: number | null }): void => {
      const unitComp = self.start_Assessment_Detail.find(el => el.competencyUnitId === payload.ucId);
      // detach(self.unit_competency_detail);
      self.unit_competency_detail = cast(unitComp);
    },
    assessment_SET_DECISION_FORM_LIST: (payload: IAssessmentApiDecisionPhaseListResponse['res']['data']): void => {
      const map = cloneDeep(
        payload.map(el => {
          const isTTDAssesse = el.files.find(ul => ul.status === 'TTD Assessee' || ul.status === 'TTD Asesi');
          return {
            id: el.id,
            isDownloadFromPortal: el.isDownloadfromPortal,
            isFilledbyAssessee: el.isFilledbyAssessee,
            isGeneratefromCms: el.isGeneratefromCms,
            isGeneratefromPortal: el.isGeneratefromPortal,
            name: el.formName,
            description: el.description,
            uploadFiles: el.files.map(ul => {
              return {
                blobId: ul.transAssFileId?.toString(),
                originalFileName: null,
                status: ul?.status || null,
                uploadDate: ul?.uploadedDate || null,
                userFileBlobId: ul?.blobId || null,
                userOriginalFileName: ul?.fileName || null,
              };
            }),
            TTDAssesse: isTTDAssesse
              ? {
                  blobId: isTTDAssesse.transAssFileId?.toString(),
                  originalFileName: null,
                  status: isTTDAssesse?.status || null,
                  uploadDate: isTTDAssesse?.uploadedDate || null,
                  userFileBlobId: isTTDAssesse?.blobId || null,
                  userOriginalFileName: isTTDAssesse?.fileName || null,
                }
              : null,
          };
        }),
      );
      self.decision_form_list = cast(map);
    },
    assessment_SET_LOADING_LIST: (payload: ISharedCommonLoadingAttrs): void => {
      const map = {
        ...self.assessment_list,
        list: self.assessment_list.list.map(list => {
          if (list.id === payload.id) {
            return {
              ...list,
              loading: {
                ...list.loading,
                [payload.type]: payload.value,
              },
            };
          } else {
            return list;
          }
        }),
      };

      detach(self.assessment_list);
      self.assessment_list = cast(cloneDeep(map));
    },
    assessment_MAP_LIST: (payload: IAssessmentApiProgressDetailResponse['res']['data']): void => {
      const map = {
        ...self.assessment_list,
        list: self.assessment_list.list.map(list => {
          if (list.id === payload.assessmentId) {
            return { ...list, phases: payload.phases, processes: payload.processes, schedule: payload.schedule };
          } else {
            return list;
          }
        }),
      };

      detach(self.assessment_list);
      self.assessment_list = cast(cloneDeep(map));
    },
    assessment_SET_DETAIL: (payload: IAssessmentApiDetailResponse['res']['data']): void => {
      self.assessment_detail = cast(payload);
    },
    assessment_REMOVE_LIST: (payload: number): void => {
      self.assessment_list = cast(
        cloneDeep({
          ...self.assessment_list,
          list: self.assessment_list.list.filter(list => list.id !== payload),
        }),
      );
    },
    assessment_CLEAR_LIST: (): void => {
      self.assessment_list = cast({});
    },
    assessment_CLEAR_DETAIL: (): void => {
      self.assessment_detail = cast({});
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch assessment list
     *
     * @param {IAssessmentGetListAttrs} payload
     *
     * @return {any} any
     */
    assessment_fetchList: flow(function* (payload: IAssessmentGetListAttrs) {
      self.assessment_SET_LOADING({ type: BASE, value: true });

      const response: IAssessmentApiListResponse = yield self.environment.assessmentApi.assessment_getList({
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      });

      if (response.ok) {
        self.assessment_SET_LIST(response.res.data, { status: payload.body.status, phase: payload.body.phase });

        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessment_SET_LOADING({ type: BASE, value: false });
    }),

    /**
     * @description Fetch assessment progress detail
     *
     * @param {IAssessmentGetProgressDetailAttrs} payload
     *
     * @return {any} any
     */
    assessment_fetchProgressDetail: flow(function* (payload: IAssessmentGetProgressDetailAttrs) {
      const { assessmentId: id } = payload.params;

      self.assessment_SET_LOADING_LIST({ id, type: BASE, value: true });

      const response: IAssessmentApiProgressDetailResponse = yield self.environment.assessmentApi.assessment_getProgressDetail({
        params: { assessmentId: id },
        requestConfig: { ...payload.requestConfig },
      });

      if (response.ok) {
        self.assessment_MAP_LIST(response.res.data);

        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessment_SET_LOADING_LIST({ id, type: BASE, value: false });
    }),

    /**
     * @description Fetch assessment detail
     *
     * @param {IAssessmentGetDetailAttrs} payload
     *
     * @return {any} any
     */
    assessment_fetchDetail: flow(function* (payload: IAssessmentGetDetailAttrs) {
      self.assessment_SET_LOADING({ type: DETAIL, value: true });

      const response: IAssessmentApiDetailResponse = yield self.environment.assessmentApi.assessment_getDetail({
        params: { ...payload.params },
        requestConfig: { ...payload.requestConfig },
      });

      if (response.ok) {
        self.assessment_SET_DETAIL(response.res.data);

        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessment_SET_LOADING({ type: DETAIL, value: false });
    }),

    /**
     * @description Cancel assessment
     *
     * @param {IAssessmentCancelAttrs} payload
     *
     * @return {any} any
     */
    assessment_cancel: flow(function* (payload: IAssessmentCancelAttrs) {
      const { assessmentId: id } = payload.params;

      self.assessment_SET_LOADING_LIST({ id, type: ACTION, value: true });

      const response: IAssessmentApiDetailResponse = yield self.environment.assessmentApi.assessment_cancel({
        params: { ...payload.params },
        requestConfig: { ...payload.requestConfig },
      });

      if (response.ok) {
        // Remove from list
        self.assessment_REMOVE_LIST(id);

        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessment_SET_LOADING_LIST({ id, type: ACTION, value: false });
    }),
    /**
     * @description Fetch assessment list
     *
     * @param {IAssessmentGetListAttrs} payload
     *
     * @return {any} any
     */
    assessment_fetchAssessmentPhaseList: flow(function* (payload: IAssessmentGetAssessmentPhaseListAttrs) {
      self.assessment_SET_LOADING({ type: BASE, value: true });

      const response: IAssessmentApiAssessmentPhaseListResponse = yield self.environment.assessmentApi.assessment_list({
        params: { ...payload.params },
        requestConfig: { ...payload.requestConfig },
      });

      if (response.ok) {
        self.assessment_SET_ASSESSMENT_PHASE_LIST(response.res.data);

        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessment_SET_LOADING({ type: BASE, value: false });
    }),
    /**
     * @description Fetch assessment start detail
     *
     * @param {IAssessmentGetListAttrs} payload
     *
     * @return {any} any
     */
    assessment_fetchAssessmentStartDetail: flow(function* (payload: IAssessmentGetAssessmentStartDetailAttrs, compId: number) {
      self.assessment_SET_LOADING({ type: BASE, value: true });

      const response: IAssessmentApiStartAssessmentDetailResponse = yield self.environment.assessmentApi.assessment_start_detail({
        params: { ...payload.params },
        requestConfig: { ...payload.requestConfig },
      });

      if (response.ok) {
        self.assessment_SET_START_ASSESSMENT_DETAIL(response.res.data, compId);

        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessment_SET_LOADING({ type: BASE, value: false });
    }),
    /**
     * @description Fetch assessment start detail
     *
     * @param {IAssessmentGetListAttrs} payload
     *
     * @return {any} any
     */
    assessment_updateAssessmentStartDetail: flow(function* (payload: IAssessmentUpdateAssessmentStartDetailAttrs) {
      self.assessment_SET_LOADING({ type: BASE, value: true });

      const response: IAssessmentApiUpdateStartAssessmentDetailResponse =
        yield self.environment.assessmentApi.assessment_update_start_detail({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        });

      if (response.ok) {
        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessment_SET_LOADING({ type: BASE, value: false });
    }),
    /**
     * @description submit assessment start detail
     *
     * @param {IAssessmentSubmitAssessmentStartDetailAttrs} payload
     *
     * @return {any} any
     */
    assessment_submitAssessmentPhase: flow(function* (payload: IAssessmentSubmitAssessmentStartDetailAttrs) {
      self.assessment_SET_LOADING({ type: ACTION, value: true });

      const response: IAssessmentApiSubmitStartAssessmentDetailResponse =
        yield self.environment.assessmentApi.assessment_submit_assessment_phase({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        });

      if (response.ok) {
        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessment_SET_LOADING({ type: ACTION, value: false });
    }),

    /**
     * @description submit assessment start detail
     *
     * @param {IAssessmentSubmitAssessmentStartDetailAttrs} payload
     *
     * @return {any} any
     */
    assessment_sendAssessmentStartDetail: flow(function* (payload: IAssessmentSubmitAssessmentStartDetailAttrs) {
      self.assessment_SET_LOADING({ type: BASE, value: true });

      const response: IAssessmentApiSubmitStartAssessmentDetailResponse =
        yield self.environment.assessmentApi.assessment_send_start_detail({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        });

      if (response.ok) {
        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessment_SET_LOADING({ type: BASE, value: false });
    }),

    /**
     * @description Fetch assessment decision detail
     *
     * @param {IAssessmentDecisionFormListAttrs} payload
     *
     * @return {any} any
     */
    assessment_fetchDecisionFormList: flow(function* (payload: IAssessmentDecisionFormListAttrs) {
      self.assessment_SET_LOADING({ type: BASE, value: true });

      const response: IAssessmentApiDecisionPhaseListResponse =
        yield self.environment.assessmentApi.assessment_decision_form_list({
          query: { ...payload.query },
          requestConfig: { ...payload.requestConfig },
        });

      if (response.ok) {
        self.assessment_SET_DECISION_FORM_LIST(response.res.data);
        if (payload?.onSuccess) payload.onSuccess();
      }
      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessment_SET_LOADING({ type: BASE, value: false });
    }),
    /**
     * @description Fetch filter phase list
     *
     * @param {IAssessmentFilterPhaseListAttrs} payload
     *
     * @return {any} any
     */
    assessment_fetchFilterPhaseList: flow(function* (payload: IAssessmentFilterPhaseListAttrs) {
      self.assessment_SET_LOADING({ type: BASE, value: true });

      const response: IAssessmentApiFilterPhaseListResponse = yield self.environment.assessmentApi.assessment_filter_phase_list({
        requestConfig: { ...payload.requestConfig },
      });

      if (response.ok) {
        self.assessment_SET_FILTER_PHASE_LIST(response.res.data);
        if (payload?.onSuccess) payload.onSuccess();
      }
      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessment_SET_LOADING({ type: BASE, value: false });
    }),
    /**
     * @description Fetch filter status list
     *
     * @param {IAssessmentFilterStatusListAttrs} payload
     *
     * @return {any} any
     */
    assessment_fetchFilterStatusList: flow(function* (payload: IAssessmentFilterStatusListAttrs) {
      self.assessment_SET_LOADING({ type: BASE, value: true });

      const response: IAssessmentApiFilterPhaseListResponse = yield self.environment.assessmentApi.assessment_filter_status_list({
        params: { ...payload.params },
        requestConfig: { ...payload.requestConfig },
      });

      if (response.ok) {
        self.assessment_SET_FILTER_STATUS_LIST(response.res.data);
        if (payload?.onSuccess) payload.onSuccess();
      }
      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessment_SET_LOADING({ type: BASE, value: false });
    }),
    /**
     * @description submit assessment decision detail
     *
     * @param {IAssessmentDecisionSubmitAttrs} payload
     *
     * @return {any} any
     */
    assessment_submitDecisionPhase: flow(function* (payload: IAssessmentDecisionSubmitAttrs) {
      self.assessment_SET_LOADING({ type: ACTION, value: true });

      const response: IAssessmentApiSubmitStartAssessmentDetailResponse =
        yield self.environment.assessmentApi.assessment_decision_submit({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        });

      if (response.ok) {
        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.assessment_SET_LOADING({ type: ACTION, value: false });
    }),
  }));

export const createAssessmentStoreDefaultModel = () => optional(AssessmentStoreModel, {});
